import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FlaskConical, Edit, Plus, Trash2, ExternalLink, FileText, Shield, AlertTriangle, Sparkles } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import sprayService from '../../services/spray';
import type { ChemicalDetail, ChemicalCropRegistration, ChemicalRegistrationRate, ChemicalPackSize, ChemicalStateRegistration } from '../../types/spray';
import { apvmaLabelUrl, apvmaPubcrisUrl } from '../../utils/apvma';

const RATE_UNITS = ['L/ha', 'mL/ha', 'kg/ha', 'g/ha', 'mL/100L', 'g/100L'] as const;

// Editable rate row used inside the registration modal (strings for inputs).
interface RateDraft {
  label: string;
  min_rate: string;
  max_rate: string;
  rate_unit: string;
  is_default: boolean;
}

function emptyRate(isDefault = false): RateDraft {
  return { label: '', min_rate: '', max_rate: '', rate_unit: 'L/ha', is_default: isDefault };
}

// Seed the modal's rate rows from an existing registration (falling back to the
// mirrored min/max/rate_unit columns for APVMA / bulk-rate registrations).
function ratesFromRegistration(existing?: ChemicalCropRegistration | null): RateDraft[] {
  if (!existing) return [emptyRate(true)];
  const rows = sprayService.effectiveRates(existing);
  if (!rows.length) return [emptyRate(true)];
  return rows.map((r, i) => ({
    label: r.label || '',
    min_rate: r.min_rate != null ? String(r.min_rate) : '',
    max_rate: r.max_rate != null ? String(r.max_rate) : '',
    rate_unit: r.rate_unit || 'L/ha',
    is_default: r.is_default ?? i === 0,
  }));
}
import ManufacturerCombobox from '../../components/spray/ManufacturerCombobox';
import SpraySituationCombobox from '../../components/spray/SpraySituationCombobox';
import ChemicalLookupModal from '../../components/spray/ChemicalLookupModal';
import ApvmaRegistrationsModal from '../../components/spray/ApvmaRegistrationsModal';
import LabelImportModal from '../../components/spray/LabelImportModal';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import ChemicalCompatibilityCard from '../../components/spray/ChemicalCompatibilityCard';
import ChemicalHistoryCard from '../../components/spray/ChemicalHistoryCard';

// ============================================================================
// Add/Edit Registration Modal
// ============================================================================

interface RegModalProps {
  chemicalId: number;
  existing?: ChemicalCropRegistration | null;
  onClose: () => void;
  onSaved: () => void;
}

function RegistrationModal({ chemicalId, existing, onClose, onSaved }: RegModalProps) {
  const [situationId, setSituationId] = useState<number | null>(existing?.situation_id ?? null);
  const [rates, setRates] = useState<RateDraft[]>(() => ratesFromRegistration(existing));
  const [form, setForm] = useState({
    registration_number: existing?.registration_number || '',
    target_pest: existing?.target_pest || '',
    whp_days: existing?.whp_days != null ? String(existing.whp_days) : '',
    whp_export_days: existing?.whp_export_days != null ? String(existing.whp_export_days) : '',
    rei_hours: existing?.rei_hours != null ? String(existing.rei_hours) : '',
    max_applications_per_season: existing?.max_applications_per_season != null ? String(existing.max_applications_per_season) : '',
    min_days_between_apps: existing?.min_days_between_apps != null ? String(existing.min_days_between_apps) : '',
    growth_stage_restriction: existing?.growth_stage_restriction || '',
    notes: existing?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);


  const f = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  // ---- Rate row helpers ----
  const updateRate = (idx: number, patch: Partial<RateDraft>) =>
    setRates(prev => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const addRate = () =>
    setRates(prev => [...prev, emptyRate(prev.length === 0)]);

  const removeRate = (idx: number) =>
    setRates(prev => {
      const next = prev.filter((_, i) => i !== idx);
      // Keep exactly one default alive.
      if (next.length && !next.some(r => r.is_default)) next[0].is_default = true;
      return next;
    });

  const setDefaultRate = (idx: number) =>
    setRates(prev => prev.map((r, i) => ({ ...r, is_default: i === idx })));

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    // Build the rates array — drop rows with neither min nor max.
    const ratePayload: ChemicalRegistrationRate[] = rates
      .filter(r => r.min_rate.trim() !== '' || r.max_rate.trim() !== '')
      .map((r, i) => ({
        label: r.label.trim() || null,
        min_rate: r.min_rate.trim() !== '' ? parseFloat(r.min_rate) : null,
        max_rate: r.max_rate.trim() !== '' ? parseFloat(r.max_rate) : null,
        rate_unit: r.rate_unit,
        is_default: r.is_default,
        sort_order: i,
      }));
    // Guarantee one default if the user never picked one.
    if (ratePayload.length && !ratePayload.some(r => r.is_default)) {
      ratePayload[0].is_default = true;
    }

    const payload: any = {
      situation_id: situationId,
      registration_number: form.registration_number || null,
      target_pest: form.target_pest || null,
      rates: ratePayload,
      whp_days: form.whp_days ? parseInt(form.whp_days) : null,
      whp_export_days: form.whp_export_days ? parseInt(form.whp_export_days) : null,
      rei_hours: form.rei_hours ? parseInt(form.rei_hours) : null,
      max_applications_per_season: form.max_applications_per_season ? parseInt(form.max_applications_per_season) : null,
      min_days_between_apps: form.min_days_between_apps ? parseInt(form.min_days_between_apps) : null,
      growth_stage_restriction: form.growth_stage_restriction || null,
      notes: form.notes || null,
    };
    // apiService throws on a 4xx/5xx — catch it so the message shows in the
    // modal instead of escaping as an unhandled rejection with Save stuck.
    try {
      const res: any = existing
        ? await sprayService.updateRegistration(chemicalId, existing.id, payload)
        : await sprayService.addRegistration(chemicalId, payload);
      if (res.success) {
        onSaved();
      } else {
        setError(res.error?.message || 'Save failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full my-4">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h3 className="text-base font-semibold text-neutral-800">
            {existing ? 'Edit Registration' : 'Add Crop Registration'}
          </h3>
        </div>
        <div className="px-6 py-4 space-y-3 max-h-[60vh] overflow-y-auto">
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1">Situation</label>
            <SpraySituationCombobox
              value={situationId}
              onChange={id => setSituationId(id)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Registration #</label>
              <input type="text" value={form.registration_number} onChange={f('registration_number')}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Target pest</label>
              <input type="text" value={form.target_pest} onChange={f('target_pest')}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          {/* Rates — one registration can carry several (e.g. a per-ha
              concentrate rate and a per-100L dilute rate). The default is
              pre-selected in the spray wizard; the operator can switch. */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-medium text-neutral-600">Rates</label>
              <button type="button" onClick={addRate}
                className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium">
                <Plus className="h-3.5 w-3.5" /> Add rate
              </button>
            </div>
            {rates.length === 0 ? (
              <p className="text-xs text-neutral-400 py-2">No rates yet — add one so this registration can be used in a spray.</p>
            ) : (
              <div className="space-y-2">
                <div className="flex gap-2 text-[11px] text-neutral-400 font-medium px-0.5">
                  <div className="w-14 text-center">Default</div>
                  <div className="flex-1">Label</div>
                  <div className="w-16">Min</div>
                  <div className="w-16">Max</div>
                  <div className="w-24">Unit</div>
                  <div className="w-6" />
                </div>
                {rates.map((r, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <div className="w-14 flex justify-center">
                      <input
                        type="radio"
                        name="default-rate"
                        checked={r.is_default}
                        onChange={() => setDefaultRate(idx)}
                        title="Use as the default rate"
                        className="text-primary-600 focus:ring-primary-500"
                      />
                    </div>
                    <input
                      type="text"
                      value={r.label}
                      onChange={e => updateRate(idx, { label: e.target.value })}
                      placeholder="e.g. Dilute"
                      className="flex-1 min-w-0 border border-neutral-300 rounded-lg px-2 py-2 text-sm"
                    />
                    <input
                      type="number" min="0" step="0.001"
                      value={r.min_rate}
                      onChange={e => updateRate(idx, { min_rate: e.target.value })}
                      placeholder="min"
                      className="w-16 border border-neutral-300 rounded-lg px-2 py-2 text-sm"
                    />
                    <input
                      type="number" min="0" step="0.001"
                      value={r.max_rate}
                      onChange={e => updateRate(idx, { max_rate: e.target.value })}
                      placeholder="max"
                      className="w-16 border border-neutral-300 rounded-lg px-2 py-2 text-sm"
                    />
                    <select
                      value={r.rate_unit}
                      onChange={e => updateRate(idx, { rate_unit: e.target.value })}
                      className="w-24 border border-neutral-300 rounded-lg px-1.5 py-2 text-sm"
                    >
                      {RATE_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                    <button
                      type="button"
                      onClick={() => removeRate(idx)}
                      className="w-6 p-1 text-neutral-400 hover:text-red-500 shrink-0"
                      title="Remove rate"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <p className="text-[11px] text-neutral-400 mt-0.5">
                  Per-100L (dilute) rates are dosed against spray-tank volume; per-ha rates against area.
                </p>
              </div>
            )}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">WHP — Harvest (days)</label>
              <input type="number" min="0" value={form.whp_days} onChange={f('whp_days')}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">WHP — Grazing (days)</label>
              <input type="number" min="0" value={form.whp_export_days} onChange={f('whp_export_days')}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">REI (hours)</label>
              <input type="number" min="0" value={form.rei_hours} onChange={f('rei_hours')}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Max apps/season</label>
              <input type="number" min="0" value={form.max_applications_per_season} onChange={f('max_applications_per_season')}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Min days between apps</label>
              <input type="number" min="0" value={form.min_days_between_apps} onChange={f('min_days_between_apps')}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1">Growth stage restriction</label>
            <input type="text" value={form.growth_stage_restriction} onChange={f('growth_stage_restriction')}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1">Notes</label>
            <textarea value={form.notes} onChange={f('notes')} rows={2}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm resize-none" />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main page
// ============================================================================

function ChemicalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [chemical, setChemical] = useState<ChemicalDetail | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [editing, setEditing] = useState(isNew);
  const [saving, setSaving] = useState(false);
  const [showLookup, setShowLookup] = useState(false);
  const { hasAccess: canManageChemicals } = useModuleLicense('spray', ['creator', 'admin']);
  // Registrations carry the WHP, REI and rates the WHP check on every spray
  // reads, so only a Spray Admin adds or changes them (HHNodeServer CLAUDE.md
  // "Each organisation owns its own spray chemicals").
  const { hasAccess: canAdminChemicals } = useModuleLicense('spray', 'admin');
  const [historyKey, setHistoryKey] = useState(0);

  // When the route changes from /new → /:id after creation, reset edit mode
  useEffect(() => {
    if (!isNew) setEditing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  const [error, setError] = useState<string | null>(null);
  const [showRegModal, setShowRegModal] = useState(false);
  const [editingReg, setEditingReg] = useState<ChemicalCropRegistration | null>(null);
  const [showApvmaImport, setShowApvmaImport] = useState(false);
  const [showLabelImport, setShowLabelImport] = useState(false);
  const [confirmingRates, setConfirmingRates] = useState(false);

  const [stateRegs, setStateRegs] = useState<ChemicalStateRegistration[]>([]);
  useEffect(() => {
    if (isNew || !id) return;
    sprayService.getStateRegistrations(Number(id)).then(r => {
      if (r.success && r.data) setStateRegs(r.data);
    });
  }, [id, isNew]);
  const [selectedRegs, setSelectedRegs] = useState<Set<number>>(new Set());

  // Registrations in the current selection whose rate came from the label and
  // has not been checked yet. Confirming is what moves a machine-read rate to
  // the same standing as a typed one, so it is deliberate and never implied by
  // editing something else.
  const selectedProposedIds = (chemical?.registrations ?? [])
    .filter(r => selectedRegs.has(r.id) && r.rate_status === 'proposed')
    .map(r => r.id);

  const confirmProposedRates = async () => {
    if (!chemical || !selectedProposedIds.length) return;
    setConfirmingRates(true);
    try {
      const res = await sprayService.confirmLabelRates(chemical.id, selectedProposedIds);
      if (res.success) {
        setSelectedRegs(new Set());
        await reloadChemical();
      }
    } finally {
      setConfirmingRates(false);
    }
  };

  const [bulkRate, setBulkRate] = useState({
    min_rate_per_ha: '', max_rate_per_ha: '', rate_unit: 'L/ha',
    whp_days: '', whp_export_days: '', rei_hours: '',
  });
  const [savingBulkRate, setSavingBulkRate] = useState(false);

  const toggleRegSel = (rid: number) => setSelectedRegs(prev => {
    const n = new Set(prev); if (n.has(rid)) n.delete(rid); else n.add(rid); return n;
  });

  const applyBulkRate = async () => {
    if (!chemical || selectedRegs.size === 0) return;
    setSavingBulkRate(true);
    const fields: Record<string, unknown> = {};
    if (bulkRate.min_rate_per_ha !== '') fields.min_rate_per_ha = Number(bulkRate.min_rate_per_ha);
    if (bulkRate.max_rate_per_ha !== '') fields.max_rate_per_ha = Number(bulkRate.max_rate_per_ha);
    if (bulkRate.rate_unit) fields.rate_unit = bulkRate.rate_unit;
    if (bulkRate.whp_days !== '') fields.whp_days = Number(bulkRate.whp_days);
    if (bulkRate.whp_export_days !== '') fields.whp_export_days = Number(bulkRate.whp_export_days);
    if (bulkRate.rei_hours !== '') fields.rei_hours = Number(bulkRate.rei_hours);
    const res = await sprayService.bulkSetRegistrationRate(chemical.id, [...selectedRegs], fields);
    setSavingBulkRate(false);
    if (res.success) {
      setSelectedRegs(new Set());
      setBulkRate({ min_rate_per_ha: '', max_rate_per_ha: '', rate_unit: 'L/ha', whp_days: '', whp_export_days: '', rei_hours: '' });
      reloadChemical();
    } else {
      setError(res.error?.message || 'Bulk rate update failed');
    }
  };

  // Pack sizes inline editing state
  const [editingPackSize, setEditingPackSize] = useState<ChemicalPackSize | null | 'new'>(null);
  const [packSizeForm, setPackSizeForm] = useState({ size: '', unit: 'L', barcode: '' });
  const [savingPackSize, setSavingPackSize] = useState(false);
  const [packSizeError, setPackSizeError] = useState<string | null>(null);

  const [form, setForm] = useState({
    product_name: '',
    trade_name: '',
    manufacturer: '',
    formulation_type: '',
    signal_word: '',
    sds_url: '',
    label_url: '',
    notes: '',
    barcode: '',
    apvma_approval_number: '',
    mode_of_action_group: '',
    mode_of_action_type: '',
    disease_activity: '',
    washoff_rain_mm: '',
    kickback_hours: '',
  });

  // Structured active ingredients — one row per ingredient
  const [activeIngredients, setActiveIngredients] = useState<
    { name: string; concentration_gl: string; concentration_pct: string; cas_number: string }[]
  >([{ name: '', concentration_gl: '', concentration_pct: '', cas_number: '' }]);

  // PPE checkboxes
  const PPE_OPTIONS = [
    { value: 'chemical_resistant_gloves',  label: 'Chemical-resistant gloves' },
    { value: 'safety_goggles',             label: 'Safety goggles' },
    { value: 'face_shield',                label: 'Face shield' },
    { value: 'respirator_p2',              label: 'Respirator P2' },
    { value: 'respirator_organic_vapour',  label: 'Respirator (organic vapour)' },
    { value: 'coveralls',                  label: 'Coveralls' },
    { value: 'waterproof_apron',           label: 'Waterproof apron' },
    { value: 'rubber_boots',               label: 'Rubber boots' },
    { value: 'washable_hat',               label: 'Washable hat' },
  ];
  const [ppeSelected, setPpeSelected] = useState<string[]>([]);

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    sprayService.getChemical(Number(id)).then(res => {
      if (res.success && res.data) {
        const c = res.data;
        setChemical(c);
        setForm({
          product_name: c.product_name,
          trade_name: c.trade_name || '',
          manufacturer: c.manufacturer || '',
          formulation_type: c.formulation_type || '',
          signal_word: c.signal_word || '',
          sds_url: c.sds_url || '',
          label_url: c.label_url || '',
          notes: c.notes || '',
          barcode: c.barcode || '',
          apvma_approval_number: c.apvma_approval_number || '',
          mode_of_action_group: c.mode_of_action_group || '',
          mode_of_action_type: c.mode_of_action_type || '',
          disease_activity: c.disease_activity || '',
          washoff_rain_mm: c.washoff_rain_mm != null ? String(c.washoff_rain_mm) : '',
          kickback_hours: c.kickback_hours != null ? String(c.kickback_hours) : '',
        });
        // Populate structured active ingredients
        if (c.active_ingredients && c.active_ingredients.length > 0) {
          setActiveIngredients(c.active_ingredients.map(ai => ({
            name: ai.name || '',
            concentration_gl: ai.concentration_gl != null ? String(ai.concentration_gl) : '',
            concentration_pct: ai.concentration_pct != null ? String(ai.concentration_pct) : '',
            cas_number: ai.cas_number || '',
          })));
        }
        // Populate PPE
        setPpeSelected(c.ppe_required || []);
      }
      setLoading(false);
    });
  }, [id, isNew]);

  // Sync edit form from the currently-loaded chemical (called when opening edit mode)
  const syncFormFromChemical = (c: ChemicalDetail) => {
    setForm({
      product_name: c.product_name,
      trade_name: c.trade_name || '',
      manufacturer: c.manufacturer || '',
      formulation_type: c.formulation_type || '',
      signal_word: c.signal_word || '',
      sds_url: c.sds_url || '',
      label_url: c.label_url || '',
      notes: c.notes || '',
      barcode: c.barcode || '',
      apvma_approval_number: c.apvma_approval_number || '',
      mode_of_action_group: c.mode_of_action_group || '',
      mode_of_action_type: c.mode_of_action_type || '',
      disease_activity: c.disease_activity || '',
      washoff_rain_mm: c.washoff_rain_mm != null ? String(c.washoff_rain_mm) : '',
      kickback_hours: c.kickback_hours != null ? String(c.kickback_hours) : '',
    });
    setActiveIngredients(
      c.active_ingredients && c.active_ingredients.length > 0
        ? c.active_ingredients.map(ai => ({
            name: ai.name || '',
            concentration_gl: ai.concentration_gl != null ? String(ai.concentration_gl) : '',
            concentration_pct: ai.concentration_pct != null ? String(ai.concentration_pct) : '',
            cas_number: ai.cas_number || '',
          }))
        : [{ name: '', concentration_gl: '', concentration_pct: '', cas_number: '' }]
    );
    setPpeSelected(c.ppe_required || []);
  };

  const f = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleSave = async () => {
    if (!form.product_name) { setError('Product name is required'); return; }
    setSaving(true);
    setError(null);

    // Build active_ingredients from structured rows — skip blank rows
    const active_ingredients = activeIngredients
      .filter(ai => ai.name.trim())
      .map(ai => ({
        name: ai.name.trim(),
        ...(ai.concentration_gl ? { concentration_gl: parseFloat(ai.concentration_gl) } : {}),
        ...(ai.concentration_pct ? { concentration_pct: parseFloat(ai.concentration_pct) } : {}),
        ...(ai.cas_number.trim() ? { cas_number: ai.cas_number.trim() } : {}),
      }));

    const payload = {
      product_name: form.product_name,
      trade_name: form.trade_name || null,
      manufacturer: form.manufacturer || null,
      formulation_type: form.formulation_type || null,
      signal_word: form.signal_word || null,
      active_ingredients,
      ppe_required: ppeSelected,
      sds_url: form.sds_url || null,
      label_url: form.label_url || null,
      notes: form.notes || null,
      barcode: form.barcode || null,
      apvma_approval_number: form.apvma_approval_number || null,
      mode_of_action_group: form.mode_of_action_group || null,
      mode_of_action_type: form.mode_of_action_type || null,
      // Blank means "work it out" (group / 25 mm default), so send null rather than 0.
      disease_activity: (form.disease_activity || null) as ChemicalDetail['disease_activity'],
      washoff_rain_mm: form.washoff_rain_mm !== '' ? Number(form.washoff_rain_mm) : null,
      kickback_hours: form.kickback_hours !== '' ? Math.round(Number(form.kickback_hours)) : null,
    };

    let res: any;
    if (isNew) {
      res = await sprayService.createChemical(payload);
    } else {
      res = await sprayService.updateChemical(Number(id), payload);
    }

    if (res.success && res.data) {
      if (isNew) {
        navigate(`/spray/chemicals/${res.data.id}`);
      } else {
        // Reload full chemical (ensures active_ingredients/ppe are properly parsed)
        reloadChemical();
        setEditing(false);
      }
    } else {
      setError(res.error?.message || 'Save failed');
    }
    setSaving(false);
  };

  const reloadChemical = () => {
    if (!id || isNew) return;
    sprayService.getChemical(Number(id)).then(res => {
      if (res.success && res.data) setChemical(res.data);
    });
    setHistoryKey(k => k + 1);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="animate-pulse space-y-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-neutral-100 rounded-lg" />)}
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={isNew ? 'New Chemical' : (chemical?.product_name || 'Chemical Detail')}
        subtitle={isNew ? 'Add to your chemicals' : chemical?.trade_name || undefined}
        icon={<FlaskConical className="h-6 w-6 text-white" />}
        actionButtons={
          !isNew && !editing ? (
            <div className="flex gap-2">
              {canManageChemicals && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowLookup(true)}
                  title="Search APVMA + manufacturer label/SDS and propose updates"
                >
                  <Sparkles className="h-4 w-4 text-purple-600" />
                  APVMA lookup
                </Button>
              )}
              {canManageChemicals && (
                <Button variant="outline" size="sm" onClick={() => { if (chemical) syncFormFromChemical(chemical); setEditing(true); }}>
                  <Edit className="h-4 w-4" />
                  Edit
                </Button>
              )}
            </div>
          ) : undefined
        }
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Edit form */}
        {editing ? (
          <div className="bg-white border border-neutral-200 rounded-xl p-6 space-y-4">
            <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">
              {isNew ? 'Product Information' : 'Edit Product'}
            </h3>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-neutral-600 mb-1">Product name *</label>
                <input type="text" value={form.product_name} onChange={f('product_name')}
                  placeholder="e.g. Agrimec 18 EC"
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-600 mb-1">Trade name</label>
                <input type="text" value={form.trade_name} onChange={f('trade_name')}
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-600 mb-1">Manufacturer</label>
                <ManufacturerCombobox
                  value={form.manufacturer}
                  onChange={val => setForm(prev => ({ ...prev, manufacturer: val }))}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-600 mb-1">Formulation type</label>
                <select value={form.formulation_type} onChange={f('formulation_type')}
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option value="">Not specified</option>
                  {[
                    ['EC', 'EC - Emulsifiable Concentrate'],
                    ['WP', 'WP - Wettable Powder'],
                    ['SC', 'SC - Suspension Concentrate'],
                    ['SL', 'SL - Soluble Liquid'],
                    ['WG', 'WG - Water Dispersible Granule'],
                    ['GR', 'GR - Granule'],
                    ['DF', 'DF - Dry Flowable'],
                  ].map(([val, label]) => <option key={val} value={val}>{label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-600 mb-1">Signal word</label>
                <select value={form.signal_word} onChange={f('signal_word')}
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option value="">Not specified</option>
                  <option value="POISON">POISON</option>
                  <option value="DANGER">DANGER</option>
                  <option value="WARNING">WARNING</option>
                  <option value="CAUTION">CAUTION</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-600 mb-1">APVMA Approval No.</label>
                <input type="text" value={form.apvma_approval_number} onChange={f('apvma_approval_number')}
                  placeholder="e.g. 92296/138632"
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-neutral-600 mb-1">Mode of Action / Group</label>
                <div className="flex gap-2">
                  <input type="text" value={form.mode_of_action_group} onChange={f('mode_of_action_group')}
                    placeholder="Group (e.g. 29)"
                    className="w-24 border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                  <select value={form.mode_of_action_type} onChange={f('mode_of_action_type')}
                    className="flex-1 border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                    <option value="">Type (optional)</option>
                    <option value="HERBICIDE">HERBICIDE</option>
                    <option value="INSECTICIDE">INSECTICIDE</option>
                    <option value="FUNGICIDE">FUNGICIDE</option>
                    <option value="MITICIDE">MITICIDE</option>
                    <option value="BACTERICIDE">BACTERICIDE</option>
                    <option value="NEMATICIDE">NEMATICIDE</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Disease protection — drives the wash-off and curative-window alerts */}
            <div className="rounded-lg border border-neutral-200 p-3 space-y-2">
              <div>
                <p className="text-xs font-medium text-neutral-600">Disease protection</p>
                <p className="text-xs text-neutral-400">
                  Leave blank to work it out: protectant or curative from the FRAC group, wash-off at 25 mm of rain.
                  Set a figure when the label or your agronomist gives one.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <label className="block">
                  <span className="block text-xs text-neutral-600 mb-1">Activity</span>
                  <select value={form.disease_activity} onChange={f('disease_activity')}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                    <option value="">From the group</option>
                    <option value="protectant">Protectant</option>
                    <option value="curative">Curative</option>
                    <option value="both">Protectant + curative</option>
                  </select>
                </label>
                <label className="block">
                  <span className="block text-xs text-neutral-600 mb-1">Washes off at (mm rain)</span>
                  <input type="number" min="1" max="500" step="0.5" value={form.washoff_rain_mm} onChange={f('washoff_rain_mm')}
                    placeholder="25 (default)"
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </label>
                <label className="block">
                  <span className="block text-xs text-neutral-600 mb-1">Kickback (hours)</span>
                  <input type="number" min="0" max="240" step="1" value={form.kickback_hours} onChange={f('kickback_hours')}
                    placeholder="From the group"
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </label>
              </div>
            </div>

            {/* Active ingredients — structured rows */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-medium text-neutral-600">Active ingredients</label>
                <button
                  type="button"
                  onClick={() => setActiveIngredients(prev => [...prev, { name: '', concentration_gl: '', concentration_pct: '', cas_number: '' }])}
                  className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium"
                >
                  <Plus className="h-3.5 w-3.5" /> Add ingredient
                </button>
              </div>
              <div className="flex gap-2 mb-1 text-xs text-neutral-400 font-medium">
                <div className="flex-1">Ingredient name</div>
                <div className="w-24">g/L</div>
                <div className="w-20">%</div>
                <div className="w-32">CAS No.</div>
                <div className="w-8" />
              </div>
              <div className="space-y-2">
                {activeIngredients.map((ai, idx) => (
                  <div key={idx} className="flex gap-2 items-start">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={ai.name}
                        onChange={e => {
                          const updated = [...activeIngredients];
                          updated[idx] = { ...updated[idx], name: e.target.value };
                          setActiveIngredients(updated);
                        }}
                        placeholder="Ingredient name (e.g. Indaziflam)"
                        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div className="w-24">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={ai.concentration_gl}
                        onChange={e => {
                          const updated = [...activeIngredients];
                          updated[idx] = { ...updated[idx], concentration_gl: e.target.value };
                          setActiveIngredients(updated);
                        }}
                        placeholder="g/L"
                        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div className="w-20">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={ai.concentration_pct}
                        onChange={e => {
                          const updated = [...activeIngredients];
                          updated[idx] = { ...updated[idx], concentration_pct: e.target.value };
                          setActiveIngredients(updated);
                        }}
                        placeholder="%"
                        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div className="w-32">
                      <input
                        type="text"
                        value={ai.cas_number}
                        onChange={e => {
                          const updated = [...activeIngredients];
                          updated[idx] = { ...updated[idx], cas_number: e.target.value };
                          setActiveIngredients(updated);
                        }}
                        placeholder="CAS No."
                        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setActiveIngredients(prev => prev.filter((_, i) => i !== idx))}
                      disabled={activeIngredients.length === 1}
                      className="mt-1 p-1.5 text-neutral-400 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-neutral-400 mt-1.5">
                Enter g/L or % — only one is needed. CAS No. is optional.
              </p>
            </div>

            {/* PPE required — checkboxes */}
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-2">PPE required</label>
              <div className="grid grid-cols-2 gap-1.5">
                {PPE_OPTIONS.map(opt => (
                  <label key={opt.value} className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={ppeSelected.includes(opt.value)}
                      onChange={e => {
                        if (e.target.checked) {
                          setPpeSelected(prev => [...prev, opt.value]);
                        } else {
                          setPpeSelected(prev => prev.filter(v => v !== opt.value));
                        }
                      }}
                      className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-neutral-700 group-hover:text-neutral-900">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">SDS URL</label>
              <input type="url" value={form.sds_url} onChange={f('sds_url')}
                placeholder="https://..."
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>

            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Label URL</label>
              <input type="url" value={form.label_url} onChange={f('label_url')}
                placeholder="https://..."
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>

            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">
                Barcode
                <span className="ml-2 text-xs font-normal text-neutral-400">(EAN-13, UPC, QR — for mobile scan lookup)</span>
              </label>
              <input type="text" value={form.barcode} onChange={f('barcode')}
                placeholder="e.g. 9312345678901"
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>

            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Notes</label>
              <textarea value={form.notes} onChange={f('notes')} rows={3}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none" />
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => isNew ? navigate('/spray/chemicals') : setEditing(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : isNew ? 'Create Chemical' : 'Save Changes'}
              </Button>
            </div>
          </div>
        ) : chemical ? (
          <>
            {/* View mode */}
            <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-lg font-bold text-neutral-900 break-words">{chemical.product_name}</h2>
                  {chemical.trade_name && <p className="text-neutral-500">{chemical.trade_name}</p>}
                  {chemical.manufacturer && <p className="text-sm text-neutral-400">{chemical.manufacturer}</p>}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {chemical.formulation_type && (
                    <span className="bg-neutral-100 text-neutral-600 text-xs font-medium px-2 py-1 rounded">
                      {chemical.formulation_type}
                    </span>
                  )}
                  {chemical.signal_word && (
                    <span className={`text-xs font-bold px-2 py-1 rounded uppercase ${
                    chemical.signal_word === 'POISON'  ? 'bg-red-100 text-red-700' :
                    chemical.signal_word === 'DANGER'  ? 'bg-red-100 text-red-700' :
                    chemical.signal_word === 'WARNING' ? 'bg-orange-100 text-orange-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                      {chemical.signal_word}
                    </span>
                  )}
                  {(chemical.mode_of_action_group || chemical.mode_of_action_type) && (
                    <span className="bg-blue-50 text-blue-700 text-xs font-medium px-2 py-1 rounded border border-blue-200">
                      {chemical.mode_of_action_group && `GROUP ${chemical.mode_of_action_group}`}
                      {chemical.mode_of_action_group && chemical.mode_of_action_type && ' '}
                      {chemical.mode_of_action_type}
                    </span>
                  )}
                </div>
              </div>

              {chemical.apvma_approval_number && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-xs font-medium text-neutral-500">APVMA Approval No:</span>
                  <code className="bg-neutral-100 px-2 py-0.5 rounded text-xs font-mono text-neutral-700">
                    {chemical.apvma_approval_number}
                  </code>
                </div>
              )}

              {(chemical.disease_activity || chemical.washoff_rain_mm != null || chemical.kickback_hours != null) && (
                <div className="text-sm text-neutral-600">
                  <span className="text-xs font-medium text-neutral-500">Disease protection: </span>
                  {[
                    chemical.disease_activity && { protectant: 'protectant', curative: 'curative', both: 'protectant + curative' }[chemical.disease_activity],
                    chemical.washoff_rain_mm != null && `washes off at ${chemical.washoff_rain_mm} mm`,
                    chemical.kickback_hours != null && `${chemical.kickback_hours} h kickback`,
                  ].filter(Boolean).join(' · ')}
                </div>
              )}

              {chemical.active_ingredients.length > 0 && (
                <div>
                  <p className="text-xs text-neutral-400 mb-1">Active ingredients</p>
                  <div className="flex flex-wrap gap-1">
                    {chemical.active_ingredients.map((ai, i) => (
                      <span key={i} className="bg-purple-50 text-purple-700 text-xs px-2 py-0.5 rounded">
                        {ai.name}
                        {ai.concentration_gl != null ? ` ${ai.concentration_gl} g/L` : ai.concentration_pct != null ? ` ${ai.concentration_pct}%` : ''}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {chemical.ppe_required && chemical.ppe_required.length > 0 && (
                <div>
                  <p className="text-xs text-neutral-400 mb-1 flex items-center gap-1">
                    <Shield className="h-3.5 w-3.5" /> PPE required
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {chemical.ppe_required.map(ppe => (
                      <span key={ppe} className="bg-amber-50 text-amber-700 text-xs px-2 py-0.5 rounded">
                        {ppe}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {chemical.sds_url && (
                <a
                  href={chemical.sds_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:underline"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Safety Data Sheet
                </a>
              )}

              {chemical.label_url && (
                <a
                  href={chemical.label_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:underline"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Product Label
                </a>
              )}

              {apvmaLabelUrl(chemical.apvma_approval_number) && (
                <a
                  href={apvmaLabelUrl(chemical.apvma_approval_number)!}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:underline"
                >
                  <FileText className="h-3.5 w-3.5" />
                  APVMA approved label (PDF)
                </a>
              )}

              {chemical.barcode && (
                <div className="flex items-center gap-2 text-sm text-neutral-600">
                  <span className="font-medium text-neutral-500">Barcode:</span>
                  <code className="bg-neutral-100 px-2 py-0.5 rounded text-xs font-mono">{chemical.barcode}</code>
                </div>
              )}

              {chemical.notes && (
                <p className="text-sm text-neutral-600 bg-neutral-50 rounded-lg px-3 py-2">{chemical.notes}</p>
              )}
            </div>

            {/* Pack Sizes */}
            <div className="bg-white border border-neutral-200 rounded-xl p-5">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">
                  Pack Sizes ({(chemical.pack_sizes || []).length})
                </h3>
                {editingPackSize === null && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPackSizeForm({ size: '', unit: 'L', barcode: '' });
                      setPackSizeError(null);
                      setEditingPackSize('new');
                    }}
                  >
                    <Plus className="h-4 w-4" />
                    Add Pack Size
                  </Button>
                )}
              </div>

              {/* Inline "new" row form */}
              {editingPackSize === 'new' && (
                <div className="border border-primary-200 bg-primary-50 rounded-lg px-4 py-3 mb-3 space-y-2">
                  <div className="flex gap-2 items-end flex-wrap">
                    <div>
                      <label className="block text-xs font-medium text-neutral-600 mb-1">Size *</label>
                      <input
                        type="number"
                        min="0"
                        step="0.001"
                        value={packSizeForm.size}
                        onChange={e => setPackSizeForm(prev => ({ ...prev, size: e.target.value }))}
                        placeholder="e.g. 5"
                        className="w-28 border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-neutral-600 mb-1">Unit</label>
                      <select
                        value={packSizeForm.unit}
                        onChange={e => setPackSizeForm(prev => ({ ...prev, unit: e.target.value }))}
                        className="border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="L">L</option>
                        <option value="mL">mL</option>
                        <option value="kg">kg</option>
                        <option value="g">g</option>
                      </select>
                    </div>
                    <div className="flex-1 min-w-40">
                      <label className="block text-xs font-medium text-neutral-600 mb-1">Barcode (optional)</label>
                      <input
                        type="text"
                        value={packSizeForm.barcode}
                        onChange={e => setPackSizeForm(prev => ({ ...prev, barcode: e.target.value }))}
                        placeholder="EAN-13, UPC, QR…"
                        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={savingPackSize}
                        onClick={async () => {
                          if (!packSizeForm.size) { setPackSizeError('Size is required'); return; }
                          setSavingPackSize(true);
                          setPackSizeError(null);
                          const res = await sprayService.createPackSize(chemical.id, {
                            size: parseFloat(packSizeForm.size),
                            unit: packSizeForm.unit,
                            barcode: packSizeForm.barcode || null,
                          });
                          setSavingPackSize(false);
                          if (res.success) {
                            setEditingPackSize(null);
                            reloadChemical();
                          } else {
                            setPackSizeError(res.error?.message || 'Save failed');
                          }
                        }}
                      >
                        {savingPackSize ? 'Saving…' : 'Save'}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => { setEditingPackSize(null); setPackSizeError(null); }}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                  {packSizeError && <p className="text-sm text-red-600">{packSizeError}</p>}
                </div>
              )}

              {/* Existing pack sizes list */}
              {(chemical.pack_sizes || []).length === 0 && editingPackSize !== 'new' ? (
                <p className="text-sm text-neutral-400 text-center py-6">No pack sizes defined</p>
              ) : (
                <div>
                  {/* Header row */}
                  {(chemical.pack_sizes || []).length > 0 && (
                    <div className="flex items-center gap-4 px-2 mb-1 text-xs font-medium text-neutral-400">
                      <div className="w-28">Size</div>
                      <div className="flex-1">Barcode</div>
                      <div className="w-16" />
                    </div>
                  )}
                  {(chemical.pack_sizes || []).map(ps => (
                    <div key={ps.id}>
                      {editingPackSize !== null && typeof editingPackSize === 'object' && editingPackSize.id === ps.id ? (
                        /* Inline edit row */
                        <div className="border border-primary-200 bg-primary-50 rounded-lg px-4 py-3 mb-2 space-y-2">
                          <div className="flex gap-2 items-end flex-wrap">
                            <div>
                              <label className="block text-xs font-medium text-neutral-600 mb-1">Size *</label>
                              <input
                                type="number"
                                min="0"
                                step="0.001"
                                value={packSizeForm.size}
                                onChange={e => setPackSizeForm(prev => ({ ...prev, size: e.target.value }))}
                                className="w-28 border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-neutral-600 mb-1">Unit</label>
                              <select
                                value={packSizeForm.unit}
                                onChange={e => setPackSizeForm(prev => ({ ...prev, unit: e.target.value }))}
                                className="border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                              >
                                <option value="L">L</option>
                                <option value="mL">mL</option>
                                <option value="kg">kg</option>
                                <option value="g">g</option>
                              </select>
                            </div>
                            <div className="flex-1 min-w-40">
                              <label className="block text-xs font-medium text-neutral-600 mb-1">Barcode (optional)</label>
                              <input
                                type="text"
                                value={packSizeForm.barcode}
                                onChange={e => setPackSizeForm(prev => ({ ...prev, barcode: e.target.value }))}
                                placeholder="EAN-13, UPC, QR…"
                                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                              />
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="primary"
                                size="sm"
                                disabled={savingPackSize}
                                onClick={async () => {
                                  if (!packSizeForm.size) { setPackSizeError('Size is required'); return; }
                                  setSavingPackSize(true);
                                  setPackSizeError(null);
                                  const res = await sprayService.updatePackSize(chemical.id, ps.id, {
                                    size: parseFloat(packSizeForm.size),
                                    unit: packSizeForm.unit,
                                    barcode: packSizeForm.barcode || null,
                                  });
                                  setSavingPackSize(false);
                                  if (res.success) {
                                    setEditingPackSize(null);
                                    reloadChemical();
                                  } else {
                                    setPackSizeError(res.error?.message || 'Save failed');
                                  }
                                }}
                              >
                                {savingPackSize ? 'Saving…' : 'Save'}
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => { setEditingPackSize(null); setPackSizeError(null); }}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                          {packSizeError && <p className="text-sm text-red-600">{packSizeError}</p>}
                        </div>
                      ) : (
                        /* Read row */
                        <div className="flex items-center gap-4 px-2 py-2 border-b border-neutral-100 last:border-b-0">
                          <div className="w-28 text-sm font-medium text-neutral-800">
                            {ps.size} {ps.unit}
                          </div>
                          <div className="flex-1 text-sm text-neutral-500">
                            {ps.barcode
                              ? <code className="bg-neutral-100 px-2 py-0.5 rounded text-xs font-mono">{ps.barcode}</code>
                              : <span className="text-neutral-300 text-xs">—</span>
                            }
                          </div>
                          <div className="w-16 flex items-center justify-end gap-1">
                            <button
                              title="Edit"
                              className="p-1 text-neutral-400 hover:text-primary-600 transition-colors"
                              onClick={() => {
                                setPackSizeForm({ size: String(ps.size), unit: ps.unit, barcode: ps.barcode || '' });
                                setPackSizeError(null);
                                setEditingPackSize(ps);
                              }}
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </button>
                            <button
                              title="Delete"
                              className="p-1 text-neutral-400 hover:text-red-500 transition-colors"
                              onClick={async () => {
                                if (!confirm(`Delete pack size ${ps.size} ${ps.unit}?`)) return;
                                await sprayService.deletePackSize(chemical.id, ps.id);
                                reloadChemical();
                              }}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* APVMA registration details */}
            {(chemical.apvma_approval_number || stateRegs.length > 0) && (
              <div className="bg-white border border-neutral-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide mb-4">
                  APVMA Registration
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs mb-4">
                  {[
                    ['APVMA #', chemical.apvma_approval_number],
                    ['Status', chemical.registration_status === 'R' ? 'Registered'
                      : chemical.registration_status === 'A' ? 'Approved (active)'
                      : chemical.registration_status],
                    ['Registered', chemical.date_registered],
                    ['Expiry', chemical.registration_expiry],
                    ['Class', chemical.product_class],
                    ['Category', chemical.category],
                    ['Poison schedule', chemical.poison_schedule ? `S${chemical.poison_schedule}` : null],
                    ['Label reg #', chemical.label_reg_number],
                  ].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k as string}>
                      <p className="text-neutral-400">{k}</p>
                      {k === 'APVMA #' ? (
                        <a
                          href={apvmaPubcrisUrl(chemical.apvma_approval_number)!}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-primary-600 hover:underline inline-flex items-center gap-1"
                        >
                          {v}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <p className="font-medium text-neutral-800">{v}</p>
                      )}
                    </div>
                  ))}
                </div>
                {chemical.withholding_text && (
                  <div className="text-xs mb-4">
                    <p className="text-neutral-400">Withholding / ESI</p>
                    <p className="text-neutral-700">{chemical.withholding_text}</p>
                  </div>
                )}
                {stateRegs.length > 0 && (
                  <div>
                    <p className="text-xs text-neutral-400 mb-1">State registration</p>
                    <div className="flex flex-wrap gap-1.5">
                      {stateRegs.map(s => (
                        <span
                          key={s.state}
                          className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${
                          s.status_code === 'R' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : s.status_code === 'S' ? 'bg-red-50 text-red-700 border-red-200'
                            : 'bg-neutral-50 text-neutral-600 border-neutral-200'
                        }`}
                          title={s.first_registered ? `First registered ${s.first_registered}` : undefined}
                        >
                          {s.state}: {s.status_label || s.status_code || '—'}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tank-mix compatibility */}
            <ChemicalCompatibilityCard
              chemical={chemical}
              canEdit={canManageChemicals}
              canDelete={canAdminChemicals}
              onSaved={reloadChemical}
            />

            {/* Crop registrations */}
            <div className="bg-white border border-neutral-200 rounded-xl p-5">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">
                  Crop Registrations ({chemical.registrations.length})
                </h3>
                <div className="flex flex-wrap gap-2">
                  {canAdminChemicals && chemical.apvma_approval_number && (
                    <Button variant="outline" size="sm" onClick={() => setShowApvmaImport(true)}>
                      <Sparkles className="h-4 w-4" />
                      Import from APVMA
                    </Button>
                  )}
                  {canManageChemicals && chemical.apvma_approval_number && (
                    <Button variant="outline" size="sm" onClick={() => setShowLabelImport(true)}>
                      <FileText className="h-4 w-4" />
                      Read label
                    </Button>
                  )}
                  {canAdminChemicals && (
                    <Button variant="outline" size="sm" onClick={() => { setEditingReg(null); setShowRegModal(true); }}>
                      <Plus className="h-4 w-4" />
                      Add Registration
                    </Button>
                  )}
                </div>
              </div>

              {!canAdminChemicals && (
                <p className="mb-4 text-xs text-neutral-500">
                  Only a Spray Admin can add or change registrations, withholding periods and rates. They are what the
                  withholding period check reads on every spray.
                </p>
              )}

              {apvmaLabelUrl(chemical.apvma_approval_number)
                && chemical.registrations.some(r => r.rate_status === 'missing') && (
                <div className="mb-4 border border-amber-200 bg-amber-50 rounded-lg p-3 flex items-start gap-2">
                  <FileText className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-900">
                    Rates aren't in the APVMA open dataset — they're only on the approved label.{' '}
                    <a
                      href={apvmaLabelUrl(chemical.apvma_approval_number)!}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium underline"
                    >
                      Open the label (PDF)
                    </a>{' '}
                    and read them off the Directions for Use table.{canAdminChemicals ? ' Tick several registrations to set one rate across all of them.' : ''}
                  </p>
                </div>
              )}

              {canAdminChemicals && selectedRegs.size > 0 && (
                <div className="mb-4 border border-purple-200 bg-purple-50 rounded-lg p-3 space-y-2">
                  <div className="text-xs font-medium text-purple-900">
                    Set rate for {selectedRegs.size} selected registration{selectedRegs.size === 1 ? '' : 's'}
                    {' '}— one rate usually covers many crops
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
                    <input type="number" step="any" placeholder="Min rate" value={bulkRate.min_rate_per_ha}
                      onChange={e => setBulkRate(b => ({ ...b, min_rate_per_ha: e.target.value }))}
                      className="border border-neutral-300 rounded px-2 py-1 text-xs" />
                    <input type="number" step="any" placeholder="Max rate" value={bulkRate.max_rate_per_ha}
                      onChange={e => setBulkRate(b => ({ ...b, max_rate_per_ha: e.target.value }))}
                      className="border border-neutral-300 rounded px-2 py-1 text-xs" />
                    <select value={bulkRate.rate_unit}
                      onChange={e => setBulkRate(b => ({ ...b, rate_unit: e.target.value }))}
                      className="border border-neutral-300 rounded px-2 py-1 text-xs">
                      {['L/ha', 'mL/ha', 'kg/ha', 'g/ha', 'mL/100L', 'g/100L'].map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                    <input type="number" placeholder="WHP harvest (d)" value={bulkRate.whp_days}
                      onChange={e => setBulkRate(b => ({ ...b, whp_days: e.target.value }))}
                      className="border border-neutral-300 rounded px-2 py-1 text-xs" />
                    <input type="number" placeholder="WHP grazing (d)" value={bulkRate.whp_export_days}
                      onChange={e => setBulkRate(b => ({ ...b, whp_export_days: e.target.value }))}
                      className="border border-neutral-300 rounded px-2 py-1 text-xs" />
                    <input type="number" placeholder="REI (h)" value={bulkRate.rei_hours}
                      onChange={e => setBulkRate(b => ({ ...b, rei_hours: e.target.value }))}
                      className="border border-neutral-300 rounded px-2 py-1 text-xs" />
                  </div>
                  <div className="flex flex-wrap justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => setSelectedRegs(new Set())}>Clear</Button>
                    {selectedProposedIds.length > 0 && (
                      <Button variant="outline" size="sm" onClick={confirmProposedRates} disabled={confirmingRates}>
                        {confirmingRates ? 'Confirming…' : `Confirm ${selectedProposedIds.length} label rate${selectedProposedIds.length === 1 ? '' : 's'}`}
                      </Button>
                    )}
                    <Button variant="primary" size="sm" onClick={applyBulkRate} disabled={savingBulkRate}>
                      {savingBulkRate ? 'Applying…' : `Apply to ${selectedRegs.size}`}
                    </Button>
                  </div>
                </div>
              )}

              {chemical.registrations.length === 0 ? (
                <p className="text-sm text-neutral-400 text-center py-6">No crop registrations yet</p>
              ) : (
                <div className="space-y-3">
                  {chemical.registrations.map(reg => (
                    <div
                      key={reg.id}
                      className="border border-neutral-200 rounded-lg p-4 space-y-2"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-2 min-w-0">
                          {canAdminChemicals && (
                            <input
                              type="checkbox"
                              checked={selectedRegs.has(reg.id)}
                              onChange={() => toggleRegSel(reg.id)}
                              aria-label={`Select ${reg.situation_name || 'registration'}${reg.target_pest ? ` — ${reg.target_pest}` : ''}`}
                              className="mt-1 rounded border-neutral-300"
                            />
                          )}
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-neutral-800">{reg.situation_name || (reg.situation_id ? `Situation #${reg.situation_id}` : 'No situation')}</p>
                              {reg.rate_status === 'missing' && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-200">
                                  Rate needed
                                </span>
                              )}
                              {reg.rate_status === 'proposed' && (
                                <span
                                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-800 border border-purple-200"
                                  title="Read off the APVMA label and not yet checked by a person."
                                >
                                  Proposed
                                </span>
                              )}
                              {reg.source === 'apvma' && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-neutral-100 text-neutral-500 border border-neutral-200">
                                  APVMA
                                </span>
                              )}
                              {reg.source === 'label' && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-neutral-100 text-neutral-500 border border-neutral-200">
                                  Label
                                </span>
                              )}
                            </div>
                            {reg.target_pest && <p className="text-sm text-neutral-500">{reg.target_pest}</p>}
                          </div>
                        </div>
                        {canAdminChemicals && (
                          <button
                            onClick={() => { setEditingReg(reg); setShowRegModal(true); }}
                            aria-label="Edit registration"
                            className="text-xs text-neutral-400 hover:text-primary-600 transition-colors shrink-0"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      {(() => {
                        const rateRows = sprayService.effectiveRates(reg);
                        if (!rateRows.length) return null;
                        return (
                          <div className="text-xs">
                            <p className="text-neutral-400 mb-0.5">{rateRows.length > 1 ? 'Rates' : 'Rate'}</p>
                            <div className="flex flex-wrap gap-1.5">
                              {rateRows.map((rt, i) => (
                                <span key={i}
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${
                                  rt.is_default
                                    ? 'bg-primary-50 text-primary-700 border-primary-200'
                                    : 'bg-neutral-50 text-neutral-600 border-neutral-200'
                                }`}>
                                  {rt.label && <span className="font-medium">{rt.label}:</span>}
                                  <span className="font-medium">
                                    {rt.min_rate != null ? `${rt.min_rate}–` : ''}{rt.max_rate ?? rt.min_rate} {rt.rate_unit}
                                  </span>
                                  {rt.is_default && rateRows.length > 1 && (
                                    <span className="text-[9px] uppercase tracking-wide text-primary-500">default</span>
                                  )}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                        {reg.whp_days != null && (
                          <div>
                            <p className="text-neutral-400">WHP Harvest</p>
                            <p className="font-medium text-amber-700">{reg.whp_days}d</p>
                          </div>
                        )}
                        {reg.whp_export_days != null && (
                          <div>
                            <p className="text-neutral-400">WHP Grazing</p>
                            <p className="font-medium text-amber-700">{reg.whp_export_days}d</p>
                          </div>
                        )}
                        {reg.rei_hours != null && (
                          <div>
                            <p className="text-neutral-400">REI</p>
                            <p className="font-medium">{reg.rei_hours}h</p>
                          </div>
                        )}
                        {reg.max_applications_per_season != null && (
                          <div>
                            <p className="text-neutral-400">Max apps/season</p>
                            <p className="font-medium">{reg.max_applications_per_season}</p>
                          </div>
                        )}
                      </div>
                      {reg.growth_stage_restriction && (
                        <div className="flex items-center gap-1 text-xs text-amber-700">
                          <AlertTriangle className="h-3.5 w-3.5" />
                          {reg.growth_stage_restriction}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <ChemicalHistoryCard chemicalId={chemical.id} reloadKey={historyKey} />
          </>
        ) : (
          <div className="text-center py-16 text-neutral-400">
            <p>Chemical not found.</p>
            <Button variant="outline" className="mt-4" onClick={() => navigate('/spray/chemicals')}>
              Back to Chemicals
            </Button>
          </div>
        )}

        {showRegModal && chemical && (
          <RegistrationModal
            chemicalId={chemical.id}
            existing={editingReg}
            onClose={() => { setShowRegModal(false); setEditingReg(null); }}
            onSaved={() => { setShowRegModal(false); setEditingReg(null); reloadChemical(); }}
          />
        )}

        {showLookup && chemical && (
          <ChemicalLookupModal
            chemicalId={chemical.id}
            canAddRegistrations={canAdminChemicals}
            productName={chemical.product_name}
            onClose={() => setShowLookup(false)}
            onApplied={() => reloadChemical()}
          />
        )}

        {showApvmaImport && chemical && (
          <ApvmaRegistrationsModal
            chemicalId={chemical.id}
            productName={chemical.product_name}
            onClose={() => setShowApvmaImport(false)}
            onImported={() => reloadChemical()}
          />
        )}

        {showLabelImport && chemical && (
          <LabelImportModal
            chemicalId={chemical.id}
            productName={chemical.product_name}
            onClose={() => setShowLabelImport(false)}
            onApplied={() => reloadChemical()}
          />
        )}
      </div>
    </>
  );
}

export default ChemicalDetailPage;
